// =============================================================================
// VINUM - Tooltip Component
// Tooltip su hover reso in PORTALE (position: fixed) così non viene tagliato
// da container con overflow:hidden (card, immagini, tabelle).
// =============================================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';

export interface TooltipProps {
  /** Tooltip text content */
  content: string;
  /** Position relative to the trigger element */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Hover delay in milliseconds before showing */
  delay?: number;
  /** Se true, il tooltip va a capo (testo lungo) invece di restare su una riga. */
  wide?: boolean;
  children: React.ReactNode;
  className?: string;
}

// Offset (px) fra trigger e bolla.
const GAP = 8;

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  position = 'top',
  delay = 200,
  wide = false,
  children,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; transform: string } | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const computePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    switch (position) {
      case 'bottom':
        setCoords({ top: r.bottom + GAP, left: cx, transform: 'translate(-50%, 0)' });
        break;
      case 'left':
        setCoords({ top: cy, left: r.left - GAP, transform: 'translate(-100%, -50%)' });
        break;
      case 'right':
        setCoords({ top: cy, left: r.right + GAP, transform: 'translate(0, -50%)' });
        break;
      case 'top':
      default:
        setCoords({ top: r.top - GAP, left: cx, transform: 'translate(-50%, -100%)' });
        break;
    }
  }, [position]);

  const show = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      computePosition();
      setOpen(true);
    }, delay);
  }, [computePosition, delay]);

  const hide = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setOpen(false);
  }, []);

  // Riposiziona su scroll/resize finché è aperto.
  useEffect(() => {
    if (!open) return;
    const onMove = () => computePosition();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, computePosition]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return (
    <span
      ref={triggerRef}
      className={cn('relative inline-flex', className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}

      {open && coords &&
        createPortal(
          <div
            role="tooltip"
            style={{ position: 'fixed', top: coords.top, left: coords.left, transform: coords.transform }}
            className={cn(
              'z-[70] pointer-events-none',
              'bg-gray-900 dark:bg-gray-700 text-white text-xs',
              'px-2.5 py-1.5 rounded-md shadow-lg',
              wide ? 'w-56 whitespace-normal text-left leading-snug' : 'whitespace-nowrap',
            )}
          >
            {content}
          </div>,
          document.body,
        )}
    </span>
  );
};

Tooltip.displayName = 'Tooltip';
